import {
  Alert,
  AloftArea,
  AloftDate,
  AloftPrice,
  AppraisalComparablePropertyInput,
  AppraisalReportInput,
  AppraisalSubjectPropertyInput,
  SiteInfluence,
  SiteView,
  addressToString,
  isDefined,
  mlsOUIDByMLSID,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState } from '../../../redux/types.ts';
import { AppraisalReportCoreData } from '../types.ts';

/**
 * Split a featureGridDataSources string into MLS (dataSource) and
 * public-record/county (verificationSource) parts.
 *
 * The string is built by joining segments with ", ". Segments containing "#"
 * are MLS entries (e.g. "STELLAR MLS #TB8420244"); all others are public-record
 * entries (e.g. "Hillsborough County").
 */
const splitDataSources = (
  raw: string | undefined,
): { dataSource?: string; verificationSource?: string } => {
  if (!raw) return {};
  const segments = raw.split(', ');
  const mlsSegments = segments.filter((s) => s.includes('#'));
  const publicSegments = segments.filter((s) => !s.includes('#'));
  return {
    dataSource: mlsSegments.length > 0 ? mlsSegments.join(', ') : undefined,
    verificationSource: publicSegments.length > 0 ? publicSegments.join(', ') : undefined,
  };
};

/**
 * Build subject property data for API export
 */
const buildSubjectProperty = (coreData: AppraisalReportCoreData): AppraisalSubjectPropertyInput => {
  const {
    subjectAddress,
    subjectAddressRaw,
    effectiveYear,
    subject,
    costApproachData,
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    opinionOfSiteValue,
    costNewsByCategories,
    depreciationsByCategories,
    priorSales,
  } = coreData;

  const subjectPriorSale = priorSales['subject']?.[0];

  return {
    fullAddress: subjectAddress ?? '',
    unitNumber: subject.editableData.propertyIdentification.address.unitNumber,
    legalDescription: subject.editableData.propertyIdentification.legalDescription,
    parcelNumber: subject.editableData.propertyIdentification.parcelNumber,
    propertyType: subject.editableData.improvements.propertyType,
    propertyInterest: subject.editableData.propertyIdentification.propertyInterest,
    yearBuilt:
      effectiveYear && subject.editableData.improvements.age
        ? parseInt(effectiveYear, 10) - subject.editableData.improvements.age
        : undefined,
    bedroomCount: subject.editableData.improvements.bedrooms,
    fullBathCount: subject.editableData.improvements.fullBaths,
    halfBathCount: subject.editableData.improvements.halfBaths,
    gla: orUndefined(AloftArea.schema.parse)(subject.editableData.improvements.gla)
      ?.toSqFt()
      .getUnitAmount(),
    lotSize: orUndefined(AloftArea.schema.parse)(subject.editableData.site.area)
      ?.toSqFt()
      .getUnitAmount(),
    opinionOfSiteValue: orUndefined(schAloftPrice.parse)(opinionOfSiteValue)?.toDollarLike(),
    valueOfImprovements: orUndefined(schAloftPrice.parse)(
      costApproachData.subject.depreciatedCostOfImprovement,
    )?.toDollarLike(),
    costNew: orUndefined(schAloftPrice.parse)(
      costApproachData.subject.totalEstimateCostNew,
    )?.toDollarLike(),
    depreciation: orUndefined(schAloftPrice.parse)(
      costApproachData.subject.depreciation.total?.mul(-1),
    )?.toDollarLike(),
    indicatedCostValue: orUndefined(schAloftPrice.parse)(
      costApproachData.subject.indicatedValueByCostApproach,
    )?.toDollarLike(),
    arvGla: orUndefined(AloftArea.schema.parse)(subject.editableData.arv?.improvements.gla)
      ?.toSqFt()
      .getUnitAmount(),
    arvValueOfImprovements: orUndefined(schAloftPrice.parse)(
      costApproachData.subjectArv?.depreciatedCostOfImprovement,
    )?.toDollarLike(),
    arvCostNew: orUndefined(schAloftPrice.parse)(
      costApproachData.subjectArv?.totalEstimateCostNew,
    )?.toDollarLike(),
    arvDepreciation: orUndefined(schAloftPrice.parse)(
      costApproachData.subjectArv?.depreciation.total,
    )?.toDollarLike(),
    arvIndicatedCostValue: orUndefined(schAloftPrice.parse)(
      costApproachData.subjectArv?.indicatedValueByCostApproach,
    )?.toDollarLike(),
    arvCraftsmanQuality: subject.editableData.arv?.improvements?.craftsmanQuality,
    uadQuality: subject.editableData.improvements.uadQuality
      ? `Q${subject.editableData.improvements.uadQuality}`
      : undefined,
    condition: subject.editableData.improvements.uadCondition
      ? `C${subject.editableData.improvements.uadCondition}`
      : undefined,
    arvUadQuality: subject.editableData.arv?.improvements.uadQuality
      ? `Q${subject.editableData.arv?.improvements.uadQuality}`
      : undefined,
    arvCondition: subject.editableData.arv?.improvements.uadCondition
      ? `C${subject.editableData.arv?.improvements.uadCondition}`
      : undefined,
    craftsmanQuality: subject.editableData.improvements.craftsmanQuality,
    neighborhoodName: subject.editableData.propertyIdentification.hoa,
    zoning: subject.editableData.propertyIdentification.zoning,
    zoningCompliance: subject.editableData.propertyIdentification.zoningCompliance,
    newConstruction: subject.editableData.propertyIdentification.newConstruction,
    pud: subject.editableData.propertyIdentification.pud,
    mandatoryFees: orUndefined(schAloftPrice.parse)(
      subject.editableData.propertyIdentification.hoaFeeAmount,
    )?.toString(true),
    mandatoryFeesFrequency: subject.editableData.propertyIdentification.hoaFeeFrequency,
    costNewBreakdown: costNewsByCategories.subject
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(costNewsByCategories.subject as Record<string, AloftPrice>).map(
              ([category, cost]) => [
                category,
                orUndefined(schAloftPrice.parse)(cost)?.toDollarLike(),
              ],
            ),
          ),
        )
      : undefined,
    depreciationBreakdown: depreciationsByCategories.subject
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(depreciationsByCategories.subject).map(([category, depreciation]) => [
              category,
              orUndefined(schAloftPrice.parse)(depreciation)?.mul(-1)?.toDollarLike(),
            ]),
          ),
        )
      : undefined,
    additionalFeaturesBreakdown: JSON.stringify(subjectSalesComparisonData?.additionalFeatures),
    arvAdditionalFeaturesBreakdown: JSON.stringify(
      subjectArvSalesComparisonData?.additionalFeatures,
    ),
    arvCostNewBreakdown: costNewsByCategories.subjectArv
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(costNewsByCategories.subjectArv as Record<string, AloftPrice>).map(
              ([category, cost]) => [
                category,
                orUndefined(schAloftPrice.parse)(cost)?.toDollarLike(),
              ],
            ),
          ),
        )
      : undefined,
    arvDepreciationBreakdown: depreciationsByCategories.subjectArv
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(depreciationsByCategories.subjectArv).map(([category, depreciation]) => [
              category,
              orUndefined(schAloftPrice.parse)(depreciation)?.mul(-1)?.toDollarLike(),
            ]),
          ),
        )
      : undefined,
    siteInfluence: subject.editableData.site.influence as SiteInfluence | undefined,
    view: subject.editableData.site.view as SiteView | undefined,
    county: subjectAddressRaw?.CountyName,
    locationType: subject.editableData.propertyIdentification.locationType,
    ...splitDataSources(subject.editableData.improvements.featureGridDataSources),
    additionalStructuresCost: orUndefined(schAloftPrice.parse)(
      costApproachData.subject.additionalStructures,
    )?.toDollarLike(),
    remainingEconomicLife: costApproachData.subject.remainingEconomicLife,
    priorSaleDate: subjectPriorSale?.CloseDate || undefined,
    priorSalePrice: subjectPriorSale?.ClosePrice || undefined,
  };
};

/**
 * Build comparable property data for API export
 */
const buildComparableProperty = (
  compId: string,
  index: number,
  coreData: AppraisalReportCoreData,
): AppraisalComparablePropertyInput | undefined => {
  const {
    effectiveYear,
    propertyDataEntities,
    adjustments,
    comparablesSalesComparisonData,
    siteValues,
    improvements,
    costNews,
    depreciations,
    indicatedCostValues,
    costNewsByCategories,
    depreciationsByCategories,
    manualAdjustmentsEntities,
    priorSales,
  } = coreData;

  const compEntity = propertyDataEntities[compId] as CompPropertyDataState | undefined;
  if (!compEntity) return undefined;

  const compPriorSale = priorSales[compId]?.[0];

  return {
    fullAddress:
      addressToString(compEntity.compData.editableData.propertyIdentification.address) ?? '',
    unitNumber: compEntity.compData.editableData.propertyIdentification.address.unitNumber,
    propertyType: compEntity.compData.editableData.improvements.propertyType,
    yearBuilt:
      effectiveYear && compEntity.compData.editableData.improvements.age
        ? parseInt(effectiveYear, 10) - compEntity.compData.editableData.improvements.age
        : undefined,
    bedroomCount: compEntity.compData.editableData.improvements.bedrooms,
    fullBathCount: compEntity.compData.editableData.improvements.fullBaths,
    halfBathCount: compEntity.compData.editableData.improvements.halfBaths,
    gla: orUndefined(AloftArea.schema.parse)(compEntity.compData.editableData.improvements.gla)
      ?.toSqFt()
      .getUnitAmount(),
    lotSize: orUndefined(AloftArea.schema.parse)(compEntity.compData.editableData.site.area)
      ?.toSqFt()
      .getUnitAmount(),
    salesPrice: orUndefined(schAloftPrice.parse)(
      compEntity.compData.editableData.transaction.price,
    )?.toDollarLike(),
    listingPrice: orUndefined(schAloftPrice.parse)(
      compEntity.compData.editableData.transaction.originalListPrice,
    )?.toDollarLike(),
    saleDate: compEntity.compData.editableData.transaction.saleDate
      ? AloftDate.fromIsoString(
          `${compEntity.compData.editableData.transaction.saleDate}-01`,
        ).toString()
      : undefined,
    contractDate: compEntity.compData.editableData.transaction.contractDate
      ? AloftDate.fromIsoString(
          `${compEntity.compData.editableData.transaction.contractDate}-01`,
        ).toString()
      : undefined,
    listingStatus: compEntity.compData.editableData.transaction.status,
    compType: compEntity.compData.compType,
    displayOrder: index,
    dateOfSaleAdjustment: orUndefined(AloftPrice.schema.parse)(
      adjustments[compId]?.dateOfSale?.adjustment,
    )?.toDollarLike(),
    siteExtractionAdjustment: orUndefined(AloftPrice.schema.parse)(
      adjustments[compId]?.site?.size?.adjustment,
    )?.toDollarLike(),
    depreciatedCostAdjustment: orUndefined(AloftPrice.schema.parse)(
      adjustments[compId]?.depreciatedCost?.adjustment,
    )?.toDollarLike(),
    totalNetAdjustment: orUndefined(AloftPrice.schema.parse)(
      adjustments[compId]?.netAdjustment?.adjustment,
    )?.toDollarLike(),
    adjustedSalesPrice: orUndefined(schAloftPrice.parse)(
      adjustments[compId]?.adjustedSalePrice?.adjustment,
    )?.toDollarLike(),
    extractedSiteValue: orUndefined(schAloftPrice.parse)(siteValues[compId])?.toDollarLike(),
    valueOfImprovements: orUndefined(schAloftPrice.parse)(improvements[compId])?.toDollarLike(),
    costNew: orUndefined(schAloftPrice.parse)(costNews[compId])?.toDollarLike(),
    depreciation: orUndefined(schAloftPrice.parse)(
      depreciations?.[compId]?.mul(-1),
    )?.toDollarLike(),
    indicatedCostValue: orUndefined(schAloftPrice.parse)(
      indicatedCostValues?.[compId],
    )?.toDollarLike(),
    proximity: comparablesSalesComparisonData?.[compId]?.proximityMiles,
    uadQuality: compEntity.compData.editableData.improvements.uadQuality
      ? `Q${compEntity.compData.editableData.improvements.uadQuality}`
      : undefined,
    condition: compEntity.compData.editableData.improvements.uadCondition
      ? `C${compEntity.compData.editableData.improvements.uadCondition}`
      : undefined,
    craftsmanQuality: compEntity.compData.editableData.improvements.craftsmanQuality,
    siteInfluence: comparablesSalesComparisonData?.[compId]?.siteInfluence as
      | SiteInfluence
      | undefined,
    view: comparablesSalesComparisonData?.[compId]?.view as SiteView | undefined,
    costNewBreakdown: costNewsByCategories[compId]
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(costNewsByCategories[compId] as Record<string, AloftPrice>).map(
              ([category, cost]) => [
                category,
                orUndefined(schAloftPrice.parse)(cost)?.toDollarLike(),
              ],
            ),
          ),
        )
      : undefined,
    depreciationBreakdown: depreciationsByCategories[compId]
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(depreciationsByCategories[compId]).map(([category, depreciation]) => [
              category,
              orUndefined(schAloftPrice.parse)(depreciation)?.mul(-1)?.toDollarLike(),
            ]),
          ),
        )
      : undefined,
    adjustmentsBreakdown: comparablesSalesComparisonData?.[compId]?.adjustments
      ? JSON.stringify(
          Object.fromEntries(
            Object.entries(comparablesSalesComparisonData[compId].adjustments).map(
              ([category, adjustment]) => [
                category,
                orUndefined(schAloftPrice.parse)(adjustment)?.toDollarLike(),
              ],
            ),
          ),
        )
      : undefined,
    additionalFeaturesBreakdown: JSON.stringify(
      comparablesSalesComparisonData?.[compId]?.additionalFeatures,
    ),
    county: compEntity.compData.editableData.propertyIdentification.address.CountyName,
    daysOnMarket: compEntity.compData.editableData.transaction.daysOnMarket,
    mlsNumber: compEntity.compData.editableData.transaction.mlsListingID,
    mlsOuid: (() => {
      const { mls } = compEntity.compData.editableData.transaction;
      if (!mls) return undefined;
      try {
        return mlsOUIDByMLSID(mls);
      } catch {
        return undefined;
      }
    })(),
    ...splitDataSources(compEntity.compData.editableData.improvements.featureGridDataSources),
    netAdjustmentPct: adjustments[compId]?.netAdjustmentPct?.value,
    grossAdjustmentPct: adjustments[compId]?.grossAdjustmentPct?.value,
    compTotalCostToCure: orUndefined(schAloftPrice.parse)(
      manualAdjustmentsEntities[compId]?.totalCostToCure,
    )?.toDollarLike(),
    priorSaleDate: compPriorSale?.CloseDate || undefined,
    priorSalePrice: compPriorSale?.ClosePrice || undefined,
  };
};

/**
 * Transform core appraisal report data into API export format
 */
export const buildApiReportData = (coreData: AppraisalReportCoreData): AppraisalReportInput => {
  const {
    order,
    effectiveDate,
    compSequence,
    alerts,
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalCostToCure,
    totalRenovationBudget,
    estimatedROI,
    valueType,
    appraiserName,
    inspectionDate,
    aivReconciliationSummarizedValues,
    signedAt,
    reconciliationPageData,
    commentaryV2,
    marketAggregateData,
    priorSales,
    adjustmentsTableData,
    costApproachData,
    mapStatistics,
    hasArvOption,
  } = coreData;

  const {
    minCompMarketAdjustedSalePrice,
    maxCompMarketAdjustedSalePrice,
    averageCompMarketAdjustedSalePrice,
    medianCompMarketAdjustedSalePrice,
    averageCompDaysOnMarket,
    medianCompDaysOnMarket,
  } = aivReconciliationSummarizedValues;

  return {
    opinionOfMarketValue: orUndefined(schAloftPrice.parse)(opinionOfMarketValue)?.toDollarLike(),
    opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
      opinionOfAfterRepairValue,
    )?.toDollarLike(),
    totalCostToCure: orUndefined(schAloftPrice.parse)(totalCostToCure)?.toDollarLike(),
    totalRenovationBudget: orUndefined(schAloftPrice.parse)(totalRenovationBudget)?.toDollarLike(),
    estimatedReturnOnInvestment: orUndefined(schAloftPrice.parse)(estimatedROI)?.toDollarLike(),
    borrower: order.borrowerName,
    productType: order.appraisalType,
    valuationType: valueType,
    inspectorType: order.inspectorType,
    effectiveDate,
    intendedUse: order.intendedUse,
    intendedUsers: order.intendedUsers,
    dismissedAlerts: Object.values(alerts)
      .flat()
      .map((alert) => (alert as Alert).type),
    clientName: order.clientName,
    appraiserName,
    appraiserCompany: order.appraisifyName,
    licenseState: order.licenseState,
    licenseNumber: order.licenseNumber,
    licenseExpiration: order.licenseExpiration
      ? AloftDate.schema.parse(order.licenseExpiration).toIsoString()
      : undefined,
    licenseType: order.licenseType,
    inspectionDate,
    costDataVersion: order.datasetVersion,
    sourceOfCostData: 'Craftsman',
    minCompAdjustedSalePrice: minCompMarketAdjustedSalePrice?.toDollarLike(),
    maxCompAdjustedSalePrice: maxCompMarketAdjustedSalePrice?.toDollarLike(),
    avgCompAdjustedSalePrice: averageCompMarketAdjustedSalePrice?.toDollarLike(),
    medianCompAdjustedSalePrice: medianCompMarketAdjustedSalePrice?.toDollarLike(),
    avgCompDaysOnMarket: averageCompDaysOnMarket,
    medianCompDaysOnMarket,
    signedAt,
    costDataEffectiveYear: costApproachData.subject.effectiveDate
      ? String(costApproachData.subject.effectiveDate)
      : undefined,
    subjectPriorSaleDate: priorSales.subject?.[0]?.CloseDate || undefined,
    subjectPriorSalePrice: priorSales.subject?.[0]?.ClosePrice || undefined,
    commentary: JSON.stringify({
      totalCostToCureCommentary: reconciliationPageData.totalCostToCureCommentary,
      highestAndBestUseCommentary: reconciliationPageData.highestAndBestUseCommentary,
      reconciliationCommentary: hasArvOption
        ? (reconciliationPageData.aivReconciliationCommentary ??
          reconciliationPageData.reconciliationCommentary)
        : reconciliationPageData.reconciliationCommentary,
      aivReconciliationCommentary: hasArvOption
        ? (reconciliationPageData.aivReconciliationCommentary ??
          reconciliationPageData.reconciliationCommentary)
        : undefined,
      arvReconciliationCommentary: hasArvOption
        ? reconciliationPageData.arvReconciliationCommentary
        : undefined,
      revisionCommentary: reconciliationPageData.revisionCommentary,
      previousRevisionCommentaries: reconciliationPageData.previousRevisionCommentaries,
      previousAlternativeComparablesCommentaries:
        reconciliationPageData.previousAlternativeComparablesCommentaries,
      feasibilityCommentary: reconciliationPageData.feasibilityCommentary,
      certificationCommentary: commentaryV2?.reconciliation?.certificationCommentary,
      roiCommentary: commentaryV2?.reconciliation?.roiCommentary,
      priorSalesCommentary: commentaryV2?.priorSales?.commentary,
      marketConditionsCommentary: commentaryV2?.marketConditionsV2?.commentary,
      siteExtractionCommentary: commentaryV2?.siteExtraction?.commentary,
      dateOfSaleAdjCommentary: adjustmentsTableData.marketAdjustments?.commentary,
      depreciatedCostCommentary: commentaryV2?.depreciatedCost
        ? JSON.stringify(commentaryV2.depreciatedCost)
        : undefined,
      alertsCommentary: commentaryV2?.alerts ? JSON.stringify(commentaryV2.alerts) : undefined,
    }),
    marketAnalysis: marketAggregateData
      ? JSON.stringify({
          demandSupply: commentaryV2?.marketConditionsV2?.estimatedDemandSupply,
          exposureTime: commentaryV2?.marketConditionsV2?.estimatedExposureTime,
          monthlyData: marketAggregateData.monthlyData.map((row) => ({
            month: row.month,
            totalComparableSales: row.total_comparable_sales,
            totalActiveProperties: row.total_active_properties,
            absorptionRate: row.absorption_rate,
            monthsSupply: row.months_supply,
            medianDaysOnMarket: row.median_days_on_market,
            medianSalePrice: row.median_sale_price
              ? AloftPrice.schema.parse(row.median_sale_price).toDollarLike()
              : undefined,
          })),
        })
      : undefined,
    marketAdjustments: JSON.stringify({
      dateOfSaleMethod: adjustmentsTableData.marketAdjustments?.method,
      listToSaleMethod: adjustmentsTableData.listToSale?.method,
    }),
    recommendedComps: JSON.stringify({ statistics: mapStatistics }),
    subjectProperty: buildSubjectProperty(coreData),
    comparableProperties: compSequence
      .map((compId: string, index: number) => buildComparableProperty(compId, index, coreData))
      .filter(isDefined),
  };
};
